import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { jobsAPI } from '../services/api';
import './ApplicationForm.css';

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  experience?: string;
  coverLetter?: string;
  resume?: string;
  submit?: string;
}

interface ApplicationResponse {
  success: boolean;
  message: string;
  application_id: number;
  account_created: boolean;
  user_email: string;
}

const ApplicationForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const jobIdParam = searchParams.get('jobId');
  const jobTitleParam = searchParams.get('job') || '';
  
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobTitle] = useState(jobTitleParam);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    phone: false,
    experience: false,
    coverLetter: false,
    resume: false,
  });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedin: '',
    portfolio: '',
    experience: '',
    currentCompany: '',
    coverLetter: '',
    resume: null as File | null,
  });
  const [applicationResponse, setApplicationResponse] = useState<ApplicationResponse | null>(null);

  useEffect(() => {
    if (jobIdParam) {
      setJobId(parseInt(jobIdParam));
    }
  }, [jobIdParam]);

  // Validation functions
  const validateFullName = (name: string): string => {
    if (!name.trim()) return 'Full name is required';
    if (name.trim().length < 2) return 'Full name must be at least 2 characters';
    if (name.trim().length > 50) return 'Full name must be less than 50 characters';
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) return 'Please enter a valid name (letters, spaces, hyphens, apostrophes only)';
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email.trim()) return 'Email address is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address (e.g., name@domain.com)';
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone.trim()) return 'Phone number is required';
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,5}[-\s.]?[0-9]{1,5}$/;
    if (!phoneRegex.test(phone.trim())) return 'Please enter a valid phone number';
    if (phone.replace(/[\s\-()]/g, '').length < 10) return 'Phone number must be at least 10 digits';
    return '';
  };

  const validateExperience = (experience: string): string => {
    if (!experience) return 'Please select your years of experience';
    return '';
  };

  const validateCoverLetter = (letter: string): string => {
    if (!letter.trim()) return 'Cover letter is required';
    if (letter.trim().length < 100) return `Cover letter must be at least 100 characters (currently ${letter.trim().length})`;
    if (letter.trim().length > 2000) return 'Cover letter must be less than 2000 characters';
    return '';
  };

  const validateResume = (file: File | null): string => {
    if (!file) return 'Please upload your resume';
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      return 'Please upload a PDF, DOC, or DOCX file';
    }
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File size exceeds 10MB limit';
    }
    return '';
  };

  const validateField = (field: keyof FormErrors, value: any): string => {
    switch (field) {
      case 'fullName': return validateFullName(value);
      case 'email': return validateEmail(value);
      case 'phone': return validatePhone(value);
      case 'experience': return validateExperience(value);
      case 'coverLetter': return validateCoverLetter(value);
      case 'resume': return validateResume(value);
      default: return '';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    let value = formData[name as keyof typeof formData];
    if (name === 'resume') {
      value = formData.resume;
    }
    const error = validateField(name as keyof FormErrors, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear field error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // If touched, validate on change
    if (touched[name as keyof typeof touched]) {
      const error = validateField(name as keyof FormErrors, value);
      setFormErrors(prev => ({ ...prev, [name]: error }));
    }
    
    if (error) setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        resume: file,
      });
      
      // Clear resume error when file is selected
      if (formErrors.resume) {
        setFormErrors(prev => ({ ...prev, resume: '' }));
      }
      
      // Validate the file
      const error = validateResume(file);
      setFormErrors(prev => ({ ...prev, resume: error }));
      setTouched(prev => ({ ...prev, resume: true }));
    } else {
      // If user cancels file selection, validate that it's required
      const error = validateResume(null);
      setFormErrors(prev => ({ ...prev, resume: error }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;
    
    // Validate all fields
    const fullNameError = validateFullName(formData.fullName);
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    const experienceError = validateExperience(formData.experience);
    const coverLetterError = validateCoverLetter(formData.coverLetter);
    const resumeError = validateResume(formData.resume);
    
    if (fullNameError) {
      errors.fullName = fullNameError;
      isValid = false;
    }
    if (emailError) {
      errors.email = emailError;
      isValid = false;
    }
    if (phoneError) {
      errors.phone = phoneError;
      isValid = false;
    }
    if (experienceError) {
      errors.experience = experienceError;
      isValid = false;
    }
    if (coverLetterError) {
      errors.coverLetter = coverLetterError;
      isValid = false;
    }
    if (resumeError) {
      errors.resume = resumeError;
      isValid = false;
    }
    
    if (!jobId) {
      errors.submit = 'Job information missing. Please go back and select a job.';
      isValid = false;
    }
    
    setFormErrors(errors);
    setTouched({
      fullName: true,
      email: true,
      phone: true,
      experience: true,
      coverLetter: true,
      resume: true,
    });
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error field
      const firstErrorField = document.querySelector('.app-form-input-error, .app-form-textarea-error, select.app-form-input-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (firstErrorField as HTMLElement).focus();
      }
      return;
    }
    
    setLoading(true);
    setError('');
    
    const submitData = new FormData();
    submitData.append('job', jobId!.toString());
    submitData.append('full_name', formData.fullName);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('cover_letter', formData.coverLetter);
    submitData.append('experience', formData.experience);
    if (formData.currentCompany) submitData.append('current_company', formData.currentCompany);
    if (formData.linkedin) submitData.append('linkedin_url', formData.linkedin);
    if (formData.portfolio) submitData.append('portfolio_url', formData.portfolio);
    if (formData.resume) submitData.append('resume', formData.resume);
    
    try {
      const response = await jobsAPI.submitApplication(submitData);
      setApplicationResponse(response.data);
      setSubmitted(true);
      
      // Auto redirect after 30 seconds if user doesn't click any button
      setTimeout(() => {
        navigate('/careers');
      }, 30000);
    } catch (err: any) {
      console.error('Error submitting application:', err);
      
      // Handle validation errors from backend
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const errorMessages: string[] = [];
        
        // Parse backend validation errors
        Object.keys(backendErrors).forEach(key => {
          if (Array.isArray(backendErrors[key])) {
            errorMessages.push(backendErrors[key].join(' '));
          } else if (typeof backendErrors[key] === 'string') {
            errorMessages.push(backendErrors[key]);
          } else if (typeof backendErrors[key] === 'object') {
            errorMessages.push(JSON.stringify(backendErrors[key]));
          }
        });
        
        setError(errorMessages.join(' | '));
      } else {
        const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to submit application. Please try again.';
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const countCharacters = (text: string): number => {
    return text.trim().length;
  };

  const characterCount = countCharacters(formData.coverLetter);
  const isCharacterCountValid = characterCount >= 100;

  // SVG Icons
  const Icons = {
    Back: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
    ),
    Check: () => (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    Warning: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 9v4M12 17h.01"/>
        <circle cx="12" cy="12" r="10"/>
      </svg>
    ),
  };

  if (submitted) {
    return (
      <>
        <Navbar />
        <div className="app-page">
          <div className="app-container">
            <div className="app-success-wrapper">
              <div className="app-success-content">
                <div className="app-success-icon">
                  <Icons.Check />
                </div>
                <h2 className="app-success-title">Application Submitted Successfully!</h2>
                
                {/* Account Created Section - Clean line style */}
                {applicationResponse?.account_created && (
                  <div className="app-account-created-line">
                    <div className="app-account-created-line-icon">🎉</div>
                    <div className="app-account-created-line-content">
                      <h4>Account Created!</h4>
                      <p>
                        An account has been automatically created with your email: <strong>{applicationResponse.user_email}</strong>
                      </p>
                      <div className="app-account-password-hint-line">
                        <span>📧</span>
                        <span>Please check your email for login instructions and password setup.</span>
                      </div>
                      <div className="app-account-actions-line">
                        <button 
                          className="app-btn-login-line" 
                          onClick={() => navigate('/login')}
                        >
                          Go to Login →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Existing User Message - Clean line style */}
                {!applicationResponse?.account_created && applicationResponse && (
                  <div className="app-existing-user-line">
                    <div className="app-existing-user-line-icon">👤</div>
                    <div className="app-existing-user-line-content">
                      <p>
                        You already have an account with us. You can login to track your application status.
                      </p>
                      <button 
                        className="app-btn-login-line" 
                        onClick={() => navigate('/login')}
                      >
                        Login to Track Application →
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="app-success-details">
                  <p className="app-success-text">
                    Thank you for applying for the <strong>{jobTitle}</strong> position.
                  </p>
                  <p className="app-success-text">
                    Our HR team will review your application and contact you within 5-7 business days.
                  </p>
                </div>
                
                <div className="app-success-actions-line">
                  <button className="app-btn-home-line" onClick={() => navigate('/careers')}>
                    View Other Jobs →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="app-page">
        <div className="app-container">
          <button className="app-back-btn" onClick={() => navigate('/careers')}>
            <Icons.Back />
            Back to Careers
          </button>

          <div className="app-header">
            <div className="app-header-badge">Apply Now</div>
            <h1 className="app-title">Apply for <span>{jobTitle}</span></h1>
            <p className="app-subtitle">
              Please fill out the form below to apply for this position
            </p>
            <div className="app-account-notice-line">
              <span>🔑</span>
              <span>An account will be automatically created with your email if you don't already have one</span>
            </div>
          </div>

          {error && (
            <div className="app-error-alert">
              <span className="app-error-icon">⚠️</span>
              {error}
            </div>
          )}

          {formErrors.submit && (
            <div className="app-error-alert">
              <span className="app-error-icon">⚠️</span>
              {formErrors.submit}
            </div>
          )}

          <form className="app-form" onSubmit={handleSubmit} noValidate>
            <div className="app-form-section">
              <h3 className="app-section-title">Personal Information</h3>
              <div className="app-form-row">
                <div className="app-form-group">
                  <label className="app-form-label">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    className={`app-form-input ${touched.fullName && formErrors.fullName ? 'app-form-input-error' : ''}`}
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    aria-invalid={!!formErrors.fullName}
                    aria-describedby="fullName-error"
                  />
                  {touched.fullName && formErrors.fullName && (
                    <div className="app-form-error" id="fullName-error">
                      <Icons.Warning />
                      {formErrors.fullName}
                    </div>
                  )}
                </div>
                <div className="app-form-group">
                  <label className="app-form-label">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    className={`app-form-input ${touched.email && formErrors.email ? 'app-form-input-error' : ''}`}
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    aria-invalid={!!formErrors.email}
                    aria-describedby="email-error"
                  />
                  {touched.email && formErrors.email && (
                    <div className="app-form-error" id="email-error">
                      <Icons.Warning />
                      {formErrors.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="app-form-row">
                <div className="app-form-group">
                  <label className="app-form-label">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    className={`app-form-input ${touched.phone && formErrors.phone ? 'app-form-input-error' : ''}`}
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    aria-invalid={!!formErrors.phone}
                    aria-describedby="phone-error"
                  />
                  {touched.phone && formErrors.phone && (
                    <div className="app-form-error" id="phone-error">
                      <Icons.Warning />
                      {formErrors.phone}
                    </div>
                  )}
                </div>
                <div className="app-form-group">
                  <label className="app-form-label">Current Company (Optional)</label>
                  <input
                    type="text"
                    name="currentCompany"
                    className="app-form-input"
                    placeholder="Current employer (optional)"
                    value={formData.currentCompany}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div className="app-form-section">
              <h3 className="app-section-title">Professional Information</h3>
              <div className="app-form-row">
                <div className="app-form-group">
                  <label className="app-form-label">Years of Experience *</label>
                  <select
                    name="experience"
                    className={`app-form-input ${touched.experience && formErrors.experience ? 'app-form-input-error' : ''}`}
                    value={formData.experience}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    aria-invalid={!!formErrors.experience}
                    aria-describedby="experience-error"
                  >
                    <option value="">Select experience</option>
                    <option value="0-1 years">0-1 years</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="5-7 years">5-7 years</option>
                    <option value="7-10 years">7-10 years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                  {touched.experience && formErrors.experience && (
                    <div className="app-form-error" id="experience-error">
                      <Icons.Warning />
                      {formErrors.experience}
                    </div>
                  )}
                </div>
                <div className="app-form-group">
                  <label className="app-form-label">LinkedIn Profile (Optional)</label>
                  <input
                    type="url"
                    name="linkedin"
                    className="app-form-input"
                    placeholder="LinkedIn profile URL (optional)"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="app-form-group">
                <label className="app-form-label">Portfolio/Website (Optional)</label>
                <input
                  type="url"
                  name="portfolio"
                  className="app-form-input"
                  placeholder="Portfolio or personal website URL (optional)"
                  value={formData.portfolio}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>

              <div className="app-form-group">
                <label className="app-form-label">Cover Letter * (Minimum 100 characters)</label>
                <textarea
                  name="coverLetter"
                  className={`app-form-textarea ${touched.coverLetter && formErrors.coverLetter ? 'app-form-textarea-error' : ''}`}
                  rows={6}
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading}
                  aria-invalid={!!formErrors.coverLetter}
                  aria-describedby="coverLetter-error"
                />
                <div className="app-char-count">
                  <span className={isCharacterCountValid ? 'app-char-count-valid' : 'app-char-count-invalid'}>
                    Characters: {characterCount} / 100
                    {characterCount > 0 && characterCount < 100 && ' (Minimum 100 characters)'}
                  </span>
                  {isCharacterCountValid && <span className="app-char-check">✓ Minimum reached</span>}
                </div>
                {touched.coverLetter && formErrors.coverLetter && (
                  <div className="app-form-error" id="coverLetter-error">
                    <Icons.Warning />
                    {formErrors.coverLetter}
                  </div>
                )}
              </div>

              <div className="app-form-group">
                <label className="app-form-label">Resume/CV *</label>
                <div className="app-file-upload-wrapper">
                  <input
                    type="file"
                    id="resume-upload"
                    className={`app-file-input ${touched.resume && formErrors.resume ? 'app-file-input-error' : ''}`}
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    aria-invalid={!!formErrors.resume}
                    aria-describedby="resume-error"
                  />
                  <label htmlFor="resume-upload" className={`app-file-upload-label ${touched.resume && formErrors.resume ? 'app-file-upload-label-error' : ''}`}>
                    <span className="app-upload-icon">📎</span>
                    Click to upload your resume (PDF, DOC, DOCX - Max 10MB)
                  </label>
                </div>
                {formData.resume && (
                  <p className="app-file-name">Selected: {formData.resume.name}</p>
                )}
                {touched.resume && formErrors.resume && (
                  <div className="app-form-error" id="resume-error">
                    <Icons.Warning />
                    {formErrors.resume}
                  </div>
                )}
              </div>
            </div>

            <div className="app-form-actions">
              <button 
                type="button" 
                className="app-btn-cancel" 
                onClick={() => navigate('/careers')}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="app-btn-submit" 
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ApplicationForm;